import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[preventNonNumericStrict]'
})
export class PreventNonNumericStrictDirective {
  // if false => integer-only (digits)
  // if true  => digits + dot, constrained by decimalPlaces
  @Input() allowDecimal: boolean = false;

  // if allowDecimal=true, controls max decimal places (0 => no dot)
  @Input() decimalPlaces: number = 0;

  // optional
  @Input() allowNegative: boolean = false;

  private allowedKeys = new Set([
    'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Home', 'End'
  ]);

  constructor(private el: ElementRef) {}

  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    // allow navigation/edit keys
    if (this.allowedKeys.has(event.key)) { return; }

    // allow ctrl/cmd shortcuts
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
      return;
    }

    const input = this.getNativeInput(event.target as any);
    if (!input) { return; }

    const key = event.key;

    // allow a single leading '-' if enabled
    if (this.allowNegative && key === '-') {
      if (input.selectionStart === 0 && !input.value.includes('-')) { return; }
      event.preventDefault();
      return;
    }

    // integer-only
    if (!this.allowDecimal) {
      if (!/^\d$/.test(key)) {
        event.preventDefault();
      }
      return;
    }

    // decimal mode: only digits or '.'
    const isDigit = key >= '0' && key <= '9';
    const isDot = key === '.';

    if (!isDigit && !isDot) {
      event.preventDefault();
      return;
    }

    // decimals not allowed => block dot
    if (this.decimalPlaces <= 0 && isDot) {
      event.preventDefault();
      return;
    }

    const next = this.applyKey(input.value, key, input.selectionStart ?? 0, input.selectionEnd ?? 0);

    if (!this.isValidDecimal(next, this.decimalPlaces, this.allowNegative)) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  blockPaste(event: ClipboardEvent) {
    const input = this.getNativeInput(event.target as any);
    if (!input) { return; }

    const paste = (event.clipboardData?.getData('text') ?? '').trim();

    if (!this.allowDecimal) {
      // integer-only: digits only
      if (!/^\d*$/.test(paste)) {
        event.preventDefault();
      }
      return;
    }

    const next = this.applyPaste(input.value, paste, input.selectionStart ?? 0, input.selectionEnd ?? 0);

    if (!this.isValidDecimal(next, this.decimalPlaces, this.allowNegative)) {
      event.preventDefault();
    }
  }

  private getNativeInput(target: HTMLElement): HTMLInputElement | null {
    // if target is the inner input already, return it
    if ((target as any).tagName === 'INPUT') {
      return target as any;
    }

    // otherwise, ion-input host: find inner input
    const host = this.el.nativeElement as HTMLElement;
    return host.querySelector('input') as HTMLInputElement | null;
  }

  private applyKey(current: string, key: string, start: number, end: number): string {
    const before = current.slice(0, start);
    const after = current.slice(end);

    // replace selection with key
    return before + key + after;
  }

  private applyPaste(current: string, paste: string, start: number, end: number): string {
    const before = current.slice(0, start);
    const after = current.slice(end);
    return before + paste + after;
  }

  private isValidDecimal(value: string, decimals: number, allowNegative: boolean): boolean {
    if (value === '') { return true; }
    if (allowNegative && value === '-') { return true; }

    // basic character whitelist
    const allowed = allowNegative ? /^-?[\d.]*$/ : /^[\d.]*$/;
    if (!allowed.test(value)) { return false; }

    // only one dot
    const dotCount = (value.match(/\./g) || []).length;
    if (dotCount > 1) { return false; }

    // if dot exists, enforce decimals length
    const dotIndex = value.indexOf('.');
    if (dotIndex >= 0) {
      const dp = value.length - dotIndex - 1;
      if (dp > decimals) { return false; }
    }

    // if decimals == 0, dot not allowed at all
    if (decimals <= 0 && value.includes('.')) { return false; }

    return true;
  }
}



// WEBPACK FOOTER //
// ./src/directives/prevent-non-numeric-strict.ts